import React, { ComponentProps, useMemo, useState } from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';
import { withI18NextTranslate } from 'sp-ui';
import { getTheme } from 'ts/common/components/gallery';
import { useBootstrapData, withBootstrapData } from 'client_react/bootstrap';
import useContract from 'client_react/common/hooks/useContract';
import { ContractPageContainer } from 'client_react/contract';

export type ClientContractData = ReturnType<typeof useContract> & {
    contractUserEmail: string | null;
    setContractUserEmail: (email: string | null) => void;
};

const App = withBootstrapData(() => {
    const { translations } = useBootstrapData();
    const { contractPublicId } = useParams<{ contractPublicId: string }>();
    const [contractUserEmail, setContractUserEmail] = useState<string | null>(null);

    const PageContainerWithTranslations = useMemo(
        () =>
            withI18NextTranslate<ComponentProps<typeof ContractPageContainer>>(
                ({ children, ...rest }: ComponentProps<typeof ContractPageContainer>) => (
                    <ContractPageContainer {...rest}>{children}</ContractPageContainer>
                ),
                translations
            ),
        [translations]
    );

    const { contract, createContract, getContract, isPending, signContract, updateContract } =
        useContract(contractPublicId);

    const contractData: ClientContractData = useMemo(
        () => ({
            contract,
            contractUserEmail,
            createContract,
            getContract,
            isPending,
            setContractUserEmail,
            signContract,
            updateContract
        }),
        [
            contract,
            contractUserEmail,
            createContract,
            getContract,
            isPending,
            setContractUserEmail,
            signContract,
            updateContract
        ]
    );

    const theme = useMemo(
        () => (contract?.brandTheme ? extendTheme(getTheme(contract.brandTheme)) : undefined),
        [contract]
    );

    if (!contract || !theme) {
        return null;
    }

    return (
        <ChakraProvider resetCSS={false} theme={theme}>
            <PageContainerWithTranslations
                contract={contract}
                contractUserEmail={contractUserEmail}
            >
                <Outlet context={contractData} />
            </PageContainerWithTranslations>
        </ChakraProvider>
    );
});

export function useClientContract() {
    return useOutletContext<ClientContractData>();
}

export default App;
